/**
 * "This is wrong."
 *
 * Telemetry records what the software did. It cannot record that the wording
 * was confusing, the number looked wrong, or the button did something other
 * than what it promised — and those are most of what is actually wrong with
 * software people use all day. This is the one channel for that, and it is
 * on every screen because the moment somebody notices is the only moment
 * they will ever bother.
 *
 * WHAT IT SENDS IS SHOWN BEFORE IT SENDS IT. The panel lists the context
 * plainly — the screen, the build, the browser, the last few things this tab
 * did — because a reporting tool that gathers unnamed "diagnostics" teaches
 * people not to use it. There is nothing here they would be surprised by.
 *
 * WHAT IT DELIBERATELY DOES NOT SEND: the page. No screenshot, no DOM, no
 * visible text. A report filed from a ticket screen would otherwise carry
 * that ticket's conversation into a table with entirely different access
 * rules. It names the screen instead; whoever reads it can open that ticket
 * under the rules that already govern it.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, Check, Loader2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { recentEvents, templatePath } from '../lib/telemetry.js';
import { Button, Popover, PopoverContent, PopoverTrigger, Tooltip } from '../ui/index.js';

/**
 * Everything the page knows about itself.
 *
 * Exported and pure so the rule that matters — that none of this is page
 * CONTENT — can be asserted in a test rather than trusted to review.
 */
export function pageContext(where: {
  path: string;
  tenant: string | null;
  theme: string;
  release: string;
  userAgent: string;
  width: number;
  height: number;
  dpr: number;
}) {
  return {
    path: where.path.slice(0, 500),
    route: templatePath(where.path),
    ...(where.tenant ? { tenant: where.tenant } : {}),
    release: where.release,
    userAgent: where.userAgent.slice(0, 400),
    viewport: `${Math.round(where.width)}x${Math.round(where.height)} @${where.dpr}x`,
    theme: where.theme,
  };
}

export function ReportBug({ tenant, theme }: { tenant: string | null; theme: string }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [why, setWhy] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const location = useLocation();

  // Focus straight into the box: the person already knows what they want to
  // say, and making them click again is how a thought gets lost.
  useEffect(() => {
    if (open) box.current?.focus();
  }, [open]);

  const release =
    (import.meta as unknown as { env?: { VITE_RELEASE?: string } }).env?.VITE_RELEASE ?? 'dev';

  const context = pageContext({
    path: location.pathname,
    tenant,
    theme,
    release,
    userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
    dpr: typeof window === 'undefined' ? 1 : (window.devicePixelRatio ?? 1),
  });

  const send = async () => {
    const said = comment.trim();
    if (said.length === 0) return;
    setState('sending');
    setWhy(null);
    try {
      await api.post('/api/v1/bugs', { comment: said, ...context, recent: recentEvents() });
      setState('sent');
      setComment('');
      // Left open for a beat so the acknowledgement is actually seen, then
      // out of the way. Somebody who reports one thing usually has one more.
      setTimeout(() => {
        setOpen(false);
        setState('idle');
      }, 1400);
    } catch (err) {
      setState('failed');
      setWhy(err instanceof Error ? err.message : 'it could not be sent');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip content="Report a problem with this page">
        <span>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" iconOnly aria-label="Report a problem">
              <Bug aria-hidden className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
        </span>
      </Tooltip>

      <PopoverContent className="w-[22rem] overflow-hidden p-0">
        <div className="border-b border-line-subtle px-3 py-2">
          <p className="text-xs font-semibold text-ink">What went wrong?</p>
          <p className="mt-0.5 text-2xs text-ink-tertiary">
            Say it however you like. What you expected and what happened is plenty.
          </p>
        </div>

        <div className="p-3">
          <textarea
            ref={box}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              // Ctrl/Cmd+Enter sends (Escape closes, via the popover).
              // Plain Enter is a new line: this is prose, and a report
              // cut off mid-sentence helps nobody.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
            }}
            rows={4}
            maxLength={4000}
            placeholder="The count on the queue tab says 12 but there are 9 rows…"
            className="w-full resize-y rounded-md border border-line bg-surface-sunken px-2 py-1.5 text-xs text-ink placeholder:text-ink-tertiary focus:border-brand focus:bg-surface focus:outline-none"
          />

          {/* Named, not implied. A tool that collects unlabelled
              "diagnostics" teaches people not to use it. */}
          <details className="mt-2">
            <summary className="cursor-pointer text-2xs text-ink-tertiary hover:text-ink-secondary">
              Sent with your message
            </summary>
            <dl className="mt-1.5 space-y-0.5 rounded bg-surface-sunken px-2 py-1.5 text-2xs">
              {[
                ['Screen', context.path],
                ['Group', context.tenant ?? 'none'],
                ['Build', context.release],
                ['Window', context.viewport],
                ['Recent actions', `${recentEvents().length} steps, names only`],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="shrink-0 text-ink-tertiary">{k}</dt>
                  <dd className="ml-auto truncate text-ink-secondary">{v}</dd>
                </div>
              ))}
              <p className="pt-1 text-ink-tertiary">
                No screenshot and nothing from the page itself.
              </p>
            </dl>
          </details>

          {why && <p className="mt-2 text-2xs text-danger">{why}</p>}

          <div className="mt-2 flex items-center gap-2">
            <Button
              size="xs"
              variant="primary"
              onClick={() => void send()}
              disabled={comment.trim().length === 0 || state === 'sending' || state === 'sent'}
            >
              {state === 'sending' && <Loader2 aria-hidden className="h-3 w-3 animate-spin" />}
              {state === 'sent' && <Check aria-hidden className="h-3 w-3" />}
              {state === 'sent' ? 'Thank you' : state === 'sending' ? 'Sending' : 'Send'}
            </Button>
            <span className="text-2xs text-ink-tertiary">Ctrl + Enter</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
